import type { AzurePickerTarget, AzureResourcePickerOptions, AzureTargetKind } from '@/types/azureResourcePicker';

const TARGET_KINDS: readonly AzureTargetKind[] = ['subscription', 'resourceGroup', 'resource'];

export function readAzureResourcePickerOptions(value: Record<string, unknown> = {}): AzureResourcePickerOptions {
  const { allowedTargetKinds, allowedResourceTypes } = value;
  if (allowedTargetKinds !== undefined && (!Array.isArray(allowedTargetKinds) ||
    !allowedTargetKinds.every((kind: unknown) => TARGET_KINDS.includes(kind as AzureTargetKind)))) {
    throw new Error('The Azure resource picker has invalid allowedTargetKinds settings.');
  }
  if (allowedResourceTypes !== undefined && (!Array.isArray(allowedResourceTypes) ||
    !allowedResourceTypes.every((type: unknown) => typeof type === 'string' && /^[\w.-]+(?:\/[\w.-]+)+$/.test(type)))) {
    throw new Error('The Azure resource picker has invalid allowedResourceTypes settings.');
  }
  return {
    ...(allowedTargetKinds !== undefined ? { allowedTargetKinds: allowedTargetKinds as AzureTargetKind[] } : {}),
    ...(allowedResourceTypes !== undefined ? { allowedResourceTypes: allowedResourceTypes as string[] } : {}),
  };
}

export function parseAzurePickerTarget(value: string): AzurePickerTarget | undefined {
  if (!value.startsWith('/') || /[?#\\]|\$\{|@\{/.test(value)) return undefined;
  const segments = value.split('/').slice(1);
  if (segments.some((segment) => !segment || segment === '.' || segment === '..')) return undefined;
  const [root, subscriptionId] = segments;
  if (root?.toLowerCase() !== 'subscriptions' || !subscriptionId ||
    !/^[\da-f]{8}-(?:[\da-f]{4}-){3}[\da-f]{12}$/i.test(subscriptionId)) return undefined;
  if (segments.length === 2) return { id: value, name: subscriptionId, kind: 'subscription', subscriptionId };
  let offset = 2;
  let resourceGroup: string | undefined;
  if (segments[offset]?.toLowerCase() === 'resourcegroups') {
    resourceGroup = segments[offset + 1];
    if (!resourceGroup) return undefined;
    offset += 2;
    if (segments.length === offset) {
      return { id: value, name: resourceGroup, kind: 'resourceGroup', subscriptionId, resourceGroup };
    }
  }
  let namespace = '';
  let types: string[] = [];
  while (offset < segments.length) {
    if (segments[offset]?.toLowerCase() === 'providers') {
      if (!segments[offset + 1] || !segments[offset + 2] || !segments[offset + 3]) return undefined;
      namespace = segments[offset + 1]!;
      types = [];
      offset += 2;
    }
    if (!namespace || !segments[offset + 1]) return undefined;
    types.push(segments[offset]!);
    offset += 2;
  }
  if (!namespace || !types.length) return undefined;
  return {
    id: value,
    name: segments[segments.length - 1]!,
    kind: 'resource',
    subscriptionId,
    ...(resourceGroup ? { resourceGroup } : {}),
    type: `${namespace}/${types.join('/')}`,
  };
}

export function isAzurePickerTargetAllowed(target: AzurePickerTarget | undefined, options: AzureResourcePickerOptions): boolean {
  if (!target) return false;
  const kinds = options.allowedTargetKinds ?? (options.allowedResourceTypes ? ['resource'] : TARGET_KINDS);
  if (!kinds.includes(target.kind)) return false;
  return target.kind !== 'resource' || options.allowedResourceTypes === undefined ||
    options.allowedResourceTypes.some((type) => type.toLowerCase() === target.type?.toLowerCase());
}